import { useShallow } from 'zustand/shallow';

import { useSignaturesIndexStore } from '@/store/docusign-modal.store';
import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import informationCircleIcon from '@ingka/ssr-icon/paths/information-circle';
import { selectDeliveryContact } from '@repo/api-adapters/selectors/customer.selectors';
import { translate } from '@repo/phrase';

import { useAsset, useAssetParams } from '@/hooks/asset.hooks';

import { useSignatureRequestMutation } from './docusign-modal.hooks';
import { SignaturesModalContent } from './signatures-modal-content/signatures-modal-content.component';
import { SignatureRequestView } from './signatures-request-view/signatures-request-view.component';

interface Props {
    visible: boolean;
    onClose: () => void;
}

export const SignaturesModal = ({ visible, onClose }: Props) => {
    const { assetId, assetType } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const initialEmail = selectDeliveryContact(data)?.email?.toLowerCase();
    const { canSubmit, submit, isPending, isCancelled } = useSignatureRequestMutation();

    const { mode, setMode, setEmail, appendixFiles, setAppendixFiles } = useSignaturesIndexStore(
        useShallow((state) => ({
            mode: state.mode,
            setMode: state.setMode,
            setEmail: state.setEmail,
            appendixFiles: state.appendixFiles,
            setAppendixFiles: state.setAppendixFiles,
        }))
    );

    const handleClose = () => {
        setAppendixFiles([]);
        onClose();
    };

    const handleSubmit = async () => {
        const isSubmitted = await submit(appendixFiles.map((row) => row.file));

        if (isSubmitted) {
            setAppendixFiles([]);
        }
    };

    const getTitle = () => {
        if (mode === 'status') {
            return translate('orderview_docusign_modal_title');
        }
        return translate('orderview_signatures_new_request');
    };

    const getBackBtnConfig = () => {
        if (mode === 'new') {
            return {
                backBtnClick: () => {
                    setMode('status');
                    setAppendixFiles([]);
                },
                ariaBackBtnTxt: translate('button.go_back'),
            };
        }
        return {};
    };

    const renderFooter = () => {
        if (mode === 'status') {
            return (
                <>
                    <Button
                        type="primary"
                        disabled={isCancelled}
                        text={translate('orderview_signatures_new_request')}
                        data-testid="signatures-request-start"
                        onClick={() => {
                            setEmail(initialEmail ?? '');
                            setMode('new');
                        }}
                    />{' '}
                    {isCancelled && (
                        <InlineMessage
                            data-testid="signatures-cancelled-order-message"
                            body={translate('orderview_signatures_cancelled_order')}
                            ssrIcon={informationCircleIcon}
                            variant="informative"
                            subtle
                        />
                    )}
                </>
            );
        }

        return (
            <>
                <Button
                    type="primary"
                    text={translate('orderview_signatures_send')}
                    disabled={!canSubmit}
                    loading={isPending}
                    data-testid="signatures-request-submit"
                    onClick={() => {
                        void handleSubmit();
                    }}
                />
            </>
        );
    };

    const renderBody = () => {
        if (mode === 'new') {
            return <SignatureRequestView loading={isPending} />;
        }
        return <SignaturesModalContent enabled={visible} />;
    };

    return (
        <Modal visible={visible} handleCloseBtn={handleClose}>
            <Sheets
                aria-label={getTitle()}
                header={<ModalHeader title={getTitle()} {...getBackBtnConfig()} />}
                footer={<ModalFooter stacked>{renderFooter()}</ModalFooter>}
            >
                <ModalBody>{renderBody()}</ModalBody>
            </Sheets>
        </Modal>
    );
};
